/**
 * Shared PAD stage-order / navigation helpers.
 *
 * Why this exists
 * ---------------
 * The ADHD Post-Assessment Discussion module historically hard-coded prev/next
 * URLs, sidebar active indexes (`currentSectionNumber - 1`), and display
 * numbers (`2.`, `3.`) in each page component. That assumes a single stage
 * order: Review → Strategies → Treatment Options → Health Checks → AMP.
 *
 * SLaM / 3 Borough need Treatment Options BEFORE Strategies. Rather than
 * duplicating swap logic across six pages, every standard PAD page asks this
 * module for:
 *   - the ordered list of stages (respecting AMP on/off)
 *   - prev/next links for the current stage
 *   - the sidebar active index
 *   - display stage numbers (so Treatment can show as "2." when it comes first)
 *
 * Firestore section keys stay stable (Strategies = '2', Treatment = '3') —
 * only display and navigation order change. Report/document section order is
 * intentionally out of scope here.
 */

// Canonical stage descriptors. `key` matches Firestore
// `5_postAssessmentDiscussion.sections` keys and never changes.
export const PAD_STAGE_DEFS = Object.freeze({
  '1': Object.freeze({
    key: '1',
    title: 'Review of Assessment',
    route: '/proforma/post-assessment-discussion/review-assessment',
  }),
  '2': Object.freeze({
    key: '2',
    title: 'Strategies and Recommendations',
    route: '/proforma/post-assessment-discussion/strategies-and-recommendations',
  }),
  '3': Object.freeze({
    key: '3',
    title: 'Treatment Options',
    route: '/proforma/post-assessment-discussion/treatment-options',
  }),
  '4': Object.freeze({
    key: '4',
    title: 'Health Checks',
    route: '/proforma/post-assessment-discussion/health-checks',
  }),
  '5': Object.freeze({
    key: '5',
    title: 'Agreed Medication Plan',
    route: '/proforma/post-assessment-discussion/agreed-medication-plan',
  }),
});

// Default clinician-facing order (non-SLaM).
const STRATEGIES_FIRST_KEYS = Object.freeze(['1', '2', '3', '4', '5']);

// SLaM / 3 Borough clinician-facing order: Treatment before Strategies.
const TREATMENT_FIRST_KEYS = Object.freeze(['1', '3', '2', '4', '5']);

/**
 * Return the ordered list of PAD stage descriptors for the current workflow.
 *
 * @param {{ isPadTreatmentFirst?: boolean, isAgreedMedicationPlanEnabled?: boolean }} opts
 * @returns {Array<{ key: string, title: string, route: string, displayNumber: number }>}
 */
export function getPadStageOrder({
  isPadTreatmentFirst = false,
  isAgreedMedicationPlanEnabled = true,
} = {}) {
  const keys = isPadTreatmentFirst ? TREATMENT_FIRST_KEYS : STRATEGIES_FIRST_KEYS;

  // AMP-disabled orgs (Well Life, SLaM) drop section 5 from the clinician
  // workflow entirely — same filter the pages already apply to the sidebar.
  const filteredKeys = isAgreedMedicationPlanEnabled
    ? keys
    : keys.filter((key) => key !== '5');

  return filteredKeys.map((key, index) => ({
    ...PAD_STAGE_DEFS[key],
    displayNumber: index + 1,
  }));
}

/**
 * Map Firestore section key → clinician-facing display stage number.
 *
 * Strategies-first: 1→1, 2→2, 3→3, 4→4, 5→5
 * Treatment-first:  1→1, 3→2, 2→3, 4→4, 5→5
 *
 * @param {boolean} isPadTreatmentFirst
 * @returns {Record<string, number>}
 */
export function getPadDisplayNumbers(isPadTreatmentFirst = false) {
  const order = getPadStageOrder({
    isPadTreatmentFirst,
    // Include AMP so key '5' still has a display number when AMP is on;
    // when AMP is off the key simply won't appear in the sidebar.
    isAgreedMedicationPlanEnabled: true,
  });
  return Object.fromEntries(order.map((stage) => [stage.key, stage.displayNumber]));
}

/**
 * Find a stage descriptor by Firestore key within an ordered stage list.
 *
 * @param {Array<{ key: string }>} order
 * @param {string} sectionKey
 */
export function findPadStage(order, sectionKey) {
  return order.find((stage) => stage.key === String(sectionKey)) || null;
}

/**
 * Sidebar active index = position in the *ordered* array, NOT
 * `sectionNumber - 1`. After a treatment-first reorder, Treatment (key '3')
 * sits at index 1, so highlighting by `3 - 1` would light up the wrong row.
 *
 * @param {Array<{ key: string }>} order
 * @param {string} sectionKey
 * @returns {number}
 */
export function getPadSidebarIndex(order, sectionKey) {
  const index = order.findIndex((stage) => stage.key === String(sectionKey));
  return index >= 0 ? index : 0;
}

/**
 * Build prev/next navigation for the current PAD page.
 *
 * Health Checks remains optional: when the clinician is on the stage that
 * owns the "Carry out health checks now?" gate (Treatment for strategies-
 * first, Strategies for treatment-first) and has NOT answered Yes, the next
 * action is Finish rather than advancing to Health Checks.
 *
 * @param {string} currentKey - Firestore section key of the current page
 * @param {Array<{ key: string, title: string, route: string }>} order
 * @param {{ isCarryOutHealthChecksNow?: boolean }} [opts]
 * @returns {{
 *   prevLink: string | null,
 *   prevTitle: string | null,
 *   nextLink: string | null,
 *   nextTitle: string | null,
 *   showFinish: boolean,
 *   displayNumber: number,
 * }}
 */
export function getPadNavLinks(currentKey, order, { isCarryOutHealthChecksNow = false } = {}) {
  const key = String(currentKey);
  const index = order.findIndex((stage) => stage.key === key);
  const current = index >= 0 ? order[index] : null;
  const prev = index > 0 ? order[index - 1] : null;
  const next = index >= 0 && index < order.length - 1 ? order[index + 1] : null;

  // The stage that owns the health-checks gate: Treatment ('3') in the
  // default order, Strategies ('2') when Treatment comes first. Finishing
  // from that stage (instead of advancing) is what ends PAD when the
  // clinician declines health checks.
  const treatmentIndex = order.findIndex((s) => s.key === '3');
  const strategiesIndex = order.findIndex((s) => s.key === '2');
  const treatmentComesFirst =
    treatmentIndex >= 0 &&
    strategiesIndex >= 0 &&
    treatmentIndex < strategiesIndex;
  const gateOwnerKey = treatmentComesFirst ? '2' : '3';

  const nextIsHealthChecks = next?.key === '4';
  const isOnGateOwner = key === gateOwnerKey;

  // On the gate-owner stage, only advance to Health Checks when the clinician
  // answered Yes. Otherwise (or when there is no next stage) show Finish.
  const shouldFinishInsteadOfNext =
    isOnGateOwner && nextIsHealthChecks && !isCarryOutHealthChecksNow;

  return {
    prevLink: prev ? prev.route : null,
    prevTitle: prev ? prev.title : null,
    nextLink: shouldFinishInsteadOfNext || !next ? null : next.route,
    nextTitle: shouldFinishInsteadOfNext || !next ? null : next.title,
    showFinish: shouldFinishInsteadOfNext || !next,
    displayNumber: current?.displayNumber ?? (index >= 0 ? index + 1 : 1),
  };
}

/**
 * Rewrite a stamped `subsectionTitleDisplay` like "2.1 Disclosure" so the
 * leading stage number matches the clinician-facing display number. Used so
 * SLaM panels show as "3.1 Disclosure" when Strategies is the third stage,
 * without mutating the patient document.
 *
 * Falls back to the original string when it has no leading "N." prefix.
 *
 * @param {string} titleDisplay
 * @param {number} displayNumber
 * @returns {string}
 */
export function rewriteSubsectionTitleDisplay(titleDisplay, displayNumber) {
  if (!titleDisplay || typeof titleDisplay !== 'string') return titleDisplay || '';
  if (!displayNumber) return titleDisplay;
  // Match "2.1 Disclosure", "3.3 Treatment Preferences", etc.
  return titleDisplay.replace(/^\d+(\.\d+\b)/, `${displayNumber}$1`);
}

/**
 * Build the sidebar sections array from the patient Firestore sections,
 * sorted into clinician-facing order and annotated with unlock flags.
 *
 * Unlock rules match the existing per-page switch:
 *   '1' always unlocked
 *   '2' / '3' unlocked when ADHD diagnosis is positive
 *   '4' unlocked when carry-out-health-checks = Yes
 *   '5' unlocked when beginning-medication = Yes (and AMP enabled)
 *
 * @param {Object} patientSections - `dataModel.5_postAssessmentDiscussion.sections`
 * @param {Array<{ key: string, route: string, title: string, displayNumber: number }>} order
 * @param {{
 *   isDiagnosisADHD?: boolean,
 *   isCarryOutHealthChecksNow?: boolean,
 *   isBeginningMedicationNow?: boolean,
 *   section1Complete?: boolean,
 *   isPadTreatmentFirst?: boolean,
 *   isTreatmentPreferencesComplete?: boolean,
 * }} unlockFlags
 */
export function getPadSidebarSections(
  patientSections,
  order,
  {
    isDiagnosisADHD = false,
    isCarryOutHealthChecksNow = false,
    isBeginningMedicationNow = false,
    section1Complete = true,
    isPadTreatmentFirst = false,
    // When treatment-first, Strategies stays locked until Treatment Options
    // 3.3.Q1 has a valid answer — so the relocated health-checks gate on DIS
    // can show or hide correctly before the clinician leaves TRT.
    isTreatmentPreferencesComplete = true,
  } = {},
) {
  const sections = patientSections || {};

  return order.map((stage) => {
    const section = sections[stage.key] || {};
    let isUnlocked = false;

    switch (stage.key) {
      case '1':
        isUnlocked = true;
        break;
      case '2':
      case '3':
        // Review pages additionally require section 1 complete before unlocking
        // later stages; callers that care pass section1Complete=false until then.
        isUnlocked = Boolean(isDiagnosisADHD) && Boolean(section1Complete);
        // Treatment-first: Strategies (key '2') requires a valid treatment
        // preferences answer on Treatment Options before the clinician can
        // advance — guarantees the DIS health-checks gate visibility is known.
        if (isPadTreatmentFirst && stage.key === '2') {
          isUnlocked = isUnlocked && Boolean(isTreatmentPreferencesComplete);
        }
        break;
      case '4':
        isUnlocked =
          Boolean(isDiagnosisADHD) &&
          Boolean(section1Complete) &&
          Boolean(isCarryOutHealthChecksNow);
        break;
      case '5':
        isUnlocked =
          Boolean(isDiagnosisADHD) &&
          Boolean(section1Complete) &&
          Boolean(isBeginningMedicationNow);
        break;
      default:
        break;
    }

    return {
      key: stage.key,
      ...section,
      // Prefer live route/title from the helper so a reordered sidebar always
      // points at the right page even if a stamped section.route were stale.
      route: stage.route || section.route,
      sectionTitle: section.sectionTitle || stage.title,
      sectionNumber: stage.displayNumber,
      isUnlocked,
    };
  });
}

/**
 * Canonical Firestore path for the "Carry out health checks now?" answer.
 * Lives on Treatment Options (section 3) regardless of which page shows the
 * UI — SLaM's synthetic Strategies panel writes this same path.
 */
export const HEALTH_CHECKS_GATE_PATH = Object.freeze({
  sectionKey: '3',
  subsectionId: '3',
  questionId: '2',
  firestoreFieldName: 'isCarryOutHealthChecksNow',
});

/**
 * Prefs that make the health-checks gate question visible (mirrors
 * conditionsToShow on the data-model question).
 */
export const HEALTH_CHECKS_GATE_PREF_VALUES = Object.freeze([
  'Only pharmacological treatment',
  'Both pharmacological and non-pharmacological treatment',
  'Not yet decided',
]);

/** Firestore path for Treatment Options 3.3.Q1 (patient treatment preferences). */
export const PATIENT_TREATMENT_PREFERENCES_PATH = Object.freeze({
  sectionKey: '3',
  subsectionId: '3',
  questionId: '1',
  firestoreFieldName: 'patientTreatmentPreferences',
});

/**
 * Read the raw "Carry out health checks now?" answer value array from the
 * stable Treatment Options 3.3.Q2 path. Returns [] when unanswered.
 *
 * @param {Object} patientDataModel
 * @returns {string[]}
 */
export function readHealthChecksGateAnswer(patientDataModel) {
  const answer =
    patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[
      HEALTH_CHECKS_GATE_PATH.sectionKey
    ]?.subsections?.[HEALTH_CHECKS_GATE_PATH.subsectionId]?.questions?.[
      HEALTH_CHECKS_GATE_PATH.questionId
    ]?.answer?.value;
  return Array.isArray(answer) ? answer : [];
}

/**
 * Read whether the clinician answered "Yes" to carry out health checks now.
 * Path is always Treatment Options 3.3.Q2 — never moves with UI reorder.
 *
 * @param {Object} patientDataModel
 * @returns {boolean}
 */
export function readIsCarryOutHealthChecksNow(patientDataModel) {
  return readHealthChecksGateAnswer(patientDataModel).includes('Yes');
}

/**
 * Resolve the health-checks gate answer from optimistic local state or Firestore.
 *
 * @param {string | null} gateAnswer - Local Strategies-page override (null = use server)
 * @param {Object} patientDataModel
 * @returns {string | null}
 */
export function resolveHealthChecksGateAnswer(gateAnswer, patientDataModel) {
  if (gateAnswer !== null && gateAnswer !== undefined) {
    return gateAnswer;
  }
  const stored = readHealthChecksGateAnswer(patientDataModel);
  return stored.length > 0 ? stored[0] : null;
}

/**
 * Whether the relocated health-checks gate has a committed Yes/No answer.
 * Used to enable Finish vs Go to Health Checks on Strategies (treatment-first).
 *
 * @param {string | null | undefined} answer
 * @returns {boolean}
 */
export function isHealthChecksGateAnswerComplete(answer) {
  return answer === 'Yes' || answer === 'No - defer';
}

/**
 * Read the patient's treatment preferences from Treatment Options 3.3.Q1.
 *
 * @param {Object} patientDataModel
 * @returns {string[]}
 */
export function readPatientTreatmentPreferences(patientDataModel) {
  const answer =
    patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[
      PATIENT_TREATMENT_PREFERENCES_PATH.sectionKey
    ]?.subsections?.[PATIENT_TREATMENT_PREFERENCES_PATH.subsectionId]?.questions?.[
      PATIENT_TREATMENT_PREFERENCES_PATH.questionId
    ]?.answer?.value;
  return Array.isArray(answer) ? answer : [];
}

/**
 * Read the full Treatment Options 3.3.Q1 answer ({ value, otherValue }).
 *
 * @param {Object} patientDataModel
 * @returns {{ value: string[], otherValue: string }}
 */
export function readPatientTreatmentPreferencesAnswer(patientDataModel) {
  const answer =
    patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[
      PATIENT_TREATMENT_PREFERENCES_PATH.sectionKey
    ]?.subsections?.[PATIENT_TREATMENT_PREFERENCES_PATH.subsectionId]?.questions?.[
      PATIENT_TREATMENT_PREFERENCES_PATH.questionId
    ]?.answer;
  return {
    value: Array.isArray(answer?.value) ? answer.value : [],
    otherValue: typeof answer?.otherValue === 'string' ? answer.otherValue : '',
  };
}

/**
 * Merge optimistic TRT formState with the persisted Firestore answer.
 *
 * @param {Object} formState
 * @param {Object} patientDataModel
 * @returns {{ value: string[], otherValue: string }}
 */
export function resolvePatientTreatmentPreferencesAnswer(formState, patientDataModel) {
  const server = readPatientTreatmentPreferencesAnswer(patientDataModel);
  const local = formState?.[PATIENT_TREATMENT_PREFERENCES_PATH.firestoreFieldName];
  if (local !== undefined) {
    return {
      value: Array.isArray(local?.value) ? local.value : [],
      otherValue: typeof local?.otherValue === 'string' ? local.otherValue : '',
    };
  }
  return server;
}

/**
 * Whether Treatment Options 3.3.Q1 is answered enough to leave TRT in
 * treatment-first mode.
 *
 * Product rule: the clinician only needs to select a treatment-preference
 * option. The free-text "Please provide details" box that appears under some
 * options is optional context — it must never block "Go to Strategies".
 *
 * @param {{ value: string[], otherValue?: string }} answer
 * @param {{ otherOptions?: Record<string, string> } | null | undefined} questionDef
 *   Kept for call-site / signature parity; not used for completeness.
 * @returns {boolean}
 */
export function isPatientTreatmentPreferencesComplete(answer, questionDef) {
  return getPatientTreatmentPreferencesProceedBlockReason(answer, questionDef) === null;
}

/**
 * Human-readable reason the Treatment Options → Strategies CTA is disabled.
 * Returns null when the clinician may proceed.
 *
 * Only an unselected preference blocks progress. Optional detail text under
 * a selected option is never required.
 *
 * @param {{ value: string[], otherValue?: string }} answer
 * @param {{ otherOptions?: Record<string, string> } | null | undefined} _questionDef
 * @returns {string|null}
 */
export function getPatientTreatmentPreferencesProceedBlockReason(answer, _questionDef) {
  const value = answer?.value;
  if (!Array.isArray(value) || value.length === 0) {
    return "Please select the patient's treatment preferences before proceeding";
  }
  return null;
}

/**
 * Convenience: read prefs from the patient doc and validate against the
 * stamped question definition on section 3.
 *
 * @param {Object} patientDataModel
 * @returns {boolean}
 */
export function isPatientTreatmentPreferencesCompleteFromModel(patientDataModel) {
  const question =
    patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[
      PATIENT_TREATMENT_PREFERENCES_PATH.sectionKey
    ]?.subsections?.[PATIENT_TREATMENT_PREFERENCES_PATH.subsectionId]?.questions?.[
      PATIENT_TREATMENT_PREFERENCES_PATH.questionId
    ];
  return isPatientTreatmentPreferencesComplete(
    readPatientTreatmentPreferencesAnswer(patientDataModel),
    question,
  );
}

/**
 * Shared unlock-flag bundle for getPadSidebarSections across PAD pages.
 * Pass `formState` on Treatment Options so sidebar + Next reflect optimistic
 * prefs before Firestore round-trips complete.
 *
 * @param {Object} opts
 * @returns {Parameters<typeof getPadSidebarSections>[2]}
 */
export function buildPadSidebarUnlockFlags({
  patientDataModel,
  isPadTreatmentFirst = false,
  isDiagnosisADHD = false,
  isCarryOutHealthChecksNow = false,
  isBeginningMedicationNow = false,
  section1Complete = true,
  formState = null,
}) {
  const prefsQuestion =
    patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[
      PATIENT_TREATMENT_PREFERENCES_PATH.sectionKey
    ]?.subsections?.[PATIENT_TREATMENT_PREFERENCES_PATH.subsectionId]?.questions?.[
      PATIENT_TREATMENT_PREFERENCES_PATH.questionId
    ];
  const prefsAnswer = formState
    ? resolvePatientTreatmentPreferencesAnswer(formState, patientDataModel)
    : readPatientTreatmentPreferencesAnswer(patientDataModel);

  return {
    isDiagnosisADHD,
    isCarryOutHealthChecksNow,
    isBeginningMedicationNow,
    section1Complete,
    isPadTreatmentFirst,
    isTreatmentPreferencesComplete:
      !isPadTreatmentFirst ||
      isPatientTreatmentPreferencesComplete(prefsAnswer, prefsQuestion),
  };
}

/**
 * Whether the health-checks gate question should be shown, given the
 * patient's treatment preferences (same rule as the data-model
 * conditionsToShow on isCarryOutHealthChecksNow).
 *
 * @param {string[]} preferences
 * @returns {boolean}
 */
export function shouldShowHealthChecksGate(preferences) {
  if (!Array.isArray(preferences) || preferences.length === 0) return false;
  return preferences.some((value) => HEALTH_CHECKS_GATE_PREF_VALUES.includes(value));
}
